import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, Calendar, Clock, Video, Phone, AlertCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import DosTimeMicButton from './DosTimeMicButton';
import LegendPopover from './LegendPopover';

/**
 * PatientSetupHeader — Feb 27, 2026 (v2)
 *
 * Two-row sticky context bar above the 2-col input/output grid.
 *
 *   Row 1: [Age] [Sex] [☐ ADSM] [☐ Medic Mode] [DOS] [Visit Type] [Appt Type]
 *   Row 2: [Reason: F/U LBP +Edit] [CC: ___________________________]
 *
 * Per provider request (Feb 27, 2026 conversation):
 *   • Encounter Type removed entirely — assume Established
 *   • Status field removed — replaced with a single ADSM checkbox
 *     (off = civilian/veteran/beneficiary, on = active_duty). That's
 *     the only distinction that actually affects orders routing.
 *   • DOS in header — locked-in for back-charting workflow
 *   • Visit Type pill cluster in header (was buried in Visit Details)
 *   • Appointment Type uses provider's curated list:
 *     F/U · Annual Wellness · Counseling · Wellness · Chronic Mgmt
 *     (PHA and Acute removed)
 *   • Medic Mode checkbox lifted from a buried row to the header
 */

const APPT_TYPES = [
  { value: 'follow_up', label: 'F/U' },
  { value: 'annual_wellness', label: 'Annual Wellness' },
  { value: 'counseling', label: 'Counseling' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'chronic_mgmt', label: 'Chronic Mgmt' },
  // Mar 1, 2026 — Added per provider request: documents Continuity of Care
  // referral requests (e.g. from CHCS/Genesis triage handoffs) as a distinct
  // appointment reason.
  { value: 'continuity_of_care_referral', label: 'Continuity of Care Referral Requested' },
];

const VISIT_PILLS = [
  { value: 'in_person_exam', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'in_person_counseling', label: 'Counseling' },
  { value: 'med_refill', label: 'Med Renewal' },
];

const PatientSetupHeader = ({
  patientAge, setPatientAge,
  patientGender, setPatientGender,
  patientStatus, setPatientStatus,
  medicMode, setMedicMode,
  dosOverride, setDosOverride,
  // Feb 28, 2026 (v6.8.48) — Late-entry / DOS-time / virtual modality controls
  // hoisted into the intake bar so high-tempo providers can flip them per
  // note without diving into the signature block.
  dosTimeOverride, setDosTimeOverride,
  lateEntry, setLateEntry,
  virtualModality, setVirtualModality,
  visitType, setVisitType,
  appointmentType, setAppointmentType,
  reasonForVisit, setReasonForVisit,
  sortedReasonOptions,
  customReasonForVisit, setCustomReasonForVisit,
  chiefComplaint, setChiefComplaint,
  autoFilledFields, setAutoFilledFields,
}) => {
  const [showReasonPicker, setShowReasonPicker] = useState(false);

  // Mar 3, 2026 — Enter-to-next-field. High-tempo providers wanted a
  // Tab-like jump on Enter key so they can drive the intake bar without
  // reaching for the mouse. Ordered list of field data-testids in the
  // sequence a provider naturally fills them.
  const FIELD_SEQUENCE = [
    'setup-header-age',
    'setup-header-sex',
    'setup-header-dos',
    'setup-header-dos-time',
    'setup-header-chief-complaint',
    'notes-textarea',  // Enter from CC jumps to the main dictation textarea.
  ];
  const handleEnterAdvance = React.useCallback((e) => {
    if (e.key !== 'Enter') return;
    // Let Enter submit a real form submit button; only intercept plain Enter
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    const currentTestId = e.target?.getAttribute?.('data-testid');
    const idx = FIELD_SEQUENCE.indexOf(currentTestId);
    if (idx < 0 || idx >= FIELD_SEQUENCE.length - 1) return;
    // Find the NEXT visible + non-disabled field, else fall through.
    for (let i = idx + 1; i < FIELD_SEQUENCE.length; i++) {
      const next = document.querySelector(`[data-testid="${FIELD_SEQUENCE[i]}"]`);
      if (next && !next.disabled) { try { next.focus(); } catch (_) { /* ignore */ } return; }
    }
  }, []);

  // Mar 3, 2026 — Belt-and-suspenders DOS-time reset on Next Patient.
  // User reported the time input occasionally persists after Next Pt even
  // though clearAll calls setDosTimeOverride(''). Whether that's a stale-
  // closure race or a browser-autofill artifact, listening for the same
  // event clearAll fires (writeright:next-patient) and defensively clearing
  // the field guarantees the visible state matches the model.
  useEffect(() => {
    const handler = () => {
      if (setDosTimeOverride) setDosTimeOverride('');
      if (setDosOverride) setDosOverride('');
    };
    window.addEventListener('writeright:next-patient', handler);
    return () => window.removeEventListener('writeright:next-patient', handler);
  }, [setDosTimeOverride, setDosOverride]);

  const selectedReasons = (reasonForVisit || '').split(',').map((v) => v.trim()).filter(Boolean);
  const labelFor = (val) => (sortedReasonOptions || []).find((o) => o.value === val)?.label || val;
  const toggleReason = (val) => {
    const next = selectedReasons.includes(val)
      ? selectedReasons.filter((v) => v !== val)
      : [...selectedReasons.filter((v) => v !== 'other'), val];
    setReasonForVisit(next.join(','));
  };
  const reasonSummary = selectedReasons.length === 0
    ? null
    : selectedReasons.length === 1
      ? labelFor(selectedReasons[0])
      : `${labelFor(selectedReasons[0])} +${selectedReasons.length - 1}`;

  const isADSM = patientStatus === 'active_duty';

  // Mar 1, 2026 — Auto-check Late Entry when the provider drops into
  // back-charting mode (DOS is a past date, not today). Only fires on the
  // TRANSITION into back-charting so the provider can still uncheck it
  // manually for the rare edge case. Resets the "previous DOS" ref so the
  // next transition fires cleanly.
  const prevDosRef = useRef(dosOverride);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const isBackCharting = !!dosOverride && dosOverride !== today;
    const wasBackCharting = !!prevDosRef.current && prevDosRef.current !== today;
    if (isBackCharting && !wasBackCharting && setLateEntry && !lateEntry) {
      setLateEntry(true);
    }
    prevDosRef.current = dosOverride;
  }, [dosOverride]);

  // Mar 1, 2026 — Render the Mode (Video/Phone) sub-toggle BESIDE the Virtual
  // pill instead of buried after Refill. Easier to remember to set the
  // modality when it sits right next to the trigger that needed it.
  const renderModeToggle = () => {
    if (!(visitType === 'virtual' || visitType === 'results_review_virtual') || !setVirtualModality) return null;
    return (
      <div
        className="flex items-center gap-1 rounded border border-blue-500/30 bg-blue-500/5 px-1 py-0.5"
        data-testid="setup-header-virtual-modality"
      >
        <span className="elegant-label text-[10px] px-0.5">Mode</span>
        <button
          type="button"
          onClick={() => {
            setVirtualModality('video');
            try { localStorage.setItem('writeright_virtual_modality', 'video'); } catch { /* ignore */ }
          }}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
            virtualModality === 'video' ? 'bg-blue-500/30 text-blue-100' : 'text-muted-foreground hover:bg-blue-500/10'
          }`}
          data-testid="setup-header-virtual-video"
          title="Video visit (audio + video)"
        >
          <Video className="w-3 h-3" /> Video
        </button>
        <button
          type="button"
          onClick={() => {
            setVirtualModality('telephone');
            try { localStorage.setItem('writeright_virtual_modality', 'telephone'); } catch { /* ignore */ }
          }}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
            virtualModality === 'telephone' ? 'bg-blue-500/30 text-blue-100' : 'text-muted-foreground hover:bg-blue-500/10'
          }`}
          data-testid="setup-header-virtual-phone"
          title="Telephone visit (audio only)"
        >
          <Phone className="w-3 h-3" /> Phone
        </button>
      </div>
    );
  };

  return (
    <div
      // Mar 2, 2026 — Sticky `top` was `0` which pinned this bar to the
      // viewport top and covered the FIXED HeaderToolbar underneath (which
      // sits at `fixed top-0 z-40`). Matched the sticky offset to the
      // <main> element's `pt-36 sm:pt-32 lg:pt-[140px]` header-clearance
      // padding so the demo bar sticks BELOW the header instead of on top
      // of it. Also dropped z-index from 40 → 30 so the fixed header wins
      // any accidental overlap.
      className="sticky top-[144px] sm:top-[128px] lg:top-[140px] z-30 mb-3 lg:mb-4 rounded-lg border border-border bg-card backdrop-blur-sm shadow-sm px-3 py-2"
      data-testid="patient-setup-header"
    >
      {/* Row 1 — demographics + flags + DOS + visit + appt */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* Age — Mar 4, 2026 (v6.9.6): emerald tint + "auto" dot when
            populated by useAutoDetect (voice-first workflow feedback).
            Clicking or editing the field clears the auto flag. */}
        <div className="flex items-center gap-1">
          <span className={`elegant-label text-[10px] ${autoFilledFields?.age ? 'text-emerald-300' : ''}`}>
            Age {autoFilledFields?.age && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ml-0.5 align-middle" data-testid="age-auto-dot" title="Auto-detected from dictation — edit to override"></span>}
          </span>
          <Input type="number" placeholder="—" value={patientAge || ''}
            onChange={(e) => {
              setPatientAge(e.target.value);
              if (autoFilledFields?.age && setAutoFilledFields) {
                setAutoFilledFields(prev => ({ ...prev, age: false }));
              }
            }}
            onKeyDown={handleEnterAdvance}
            className={`h-8 w-[4.5rem] text-sm px-2 rounded ${autoFilledFields?.age ? 'border-emerald-500/50 bg-emerald-500/5' : ''}`}
            data-testid="setup-header-age"
          />
        </div>
        {/* Sex — same auto-fill visual treatment. */}
        <div className="flex items-center gap-1">
          <span className={`elegant-label text-[10px] ${autoFilledFields?.gender ? 'text-emerald-300' : ''}`}>
            Sex {autoFilledFields?.gender && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 ml-0.5 align-middle" data-testid="sex-auto-dot" title="Auto-detected from dictation — edit to override"></span>}
          </span>
          <select value={patientGender || ''}
            onChange={(e) => {
              setPatientGender(e.target.value);
              if (autoFilledFields?.gender && setAutoFilledFields) {
                setAutoFilledFields(prev => ({ ...prev, gender: false }));
              }
            }}
            onKeyDown={handleEnterAdvance}
            className={`h-8 px-1.5 text-sm rounded border bg-input text-foreground ${autoFilledFields?.gender ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border'}`}
            data-testid="setup-header-sex"
          >
            <option value="">—</option>
            <option value="male">M</option>
            <option value="female">F</option>
            <option value="other">Other</option>
          </select>
        </div>
        {/* ADSM checkbox — replaces Status. ON = active_duty, OFF = civ/vet/dep */}
        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Active Duty Service Member — matters for orders routing">
          <input type="checkbox" checked={isADSM}
            onChange={(e) => setPatientStatus(e.target.checked ? 'active_duty' : '')}
            className="w-3.5 h-3.5"
            data-testid="setup-header-adsm"
          />
          <span className={`text-[11px] font-semibold ${isADSM ? 'text-emerald-300' : 'text-foreground/70'}`}>ADSM</span>
        </label>
        {/* Mar 4, 2026 (v6.9.7) — Visual signal legend "?" icon. */}
        <LegendPopover />
        {/* Medic Mode checkbox */}
        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Medic Mode — annotates dictation as the medic, provider signs">
          <input type="checkbox" checked={!!medicMode}
            onChange={(e) => setMedicMode(e.target.checked)}
            className="w-3.5 h-3.5"
            data-testid="setup-header-medic-mode"
          />
          <span className={`text-[11px] font-semibold ${medicMode ? 'text-emerald-300' : 'text-foreground/70'}`}>Medic Mode</span>
        </label>
        {/* DOS — Date of Service (essential for back-charting).
            v6.8.48 — Now bundled with: time-of-service free field,
            Late-Entry checkbox. High-tempo providers don't have time to
            dive into the signature block for every catch-up note. */}
        {/* Mar 1, 2026 — Compact DOS block. Date + Time now sit as a
            single tight pair (date field tightened to a fixed width so it
            doesn't sprawl). Late Entry stays inline on the same row so the
            whole encounter-stamp lives on ONE line instead of wrapping. */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="setup-header-dos-block">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="elegant-label text-[10px]">DOS</span>
          <Input type="date" value={dosOverride || ''}
            onChange={(e) => setDosOverride && setDosOverride(e.target.value)}
            onKeyDown={handleEnterAdvance}
            className={`h-8 text-xs px-1.5 rounded w-[125px] ${dosOverride ? 'border-emerald-500/50 bg-emerald-500/5' : ''}`}
            data-testid="setup-header-dos"
            title={dosOverride ? `Back-charting: DOS locked to ${dosOverride}` : 'Set DOS for back-charting (leave empty = today)'}
          />
          {/* Mar 2, 2026 — Switched from `type="time"` to `type="text"` with
              military-24hr auto-format. Native HTML time picker was showing
              12hr + AM/PM per user's system locale; provider wanted
              military-style raw entry ("1145" → "11:45"). Accepts:
                - "1145"     → "11:45"
                - "11:45"    → "11:45" (already formatted)
                - "0730"     → "07:30"
                - "9:15"     → "09:15" (pads hour)
                - anything invalid gets held until valid input.
              Rejects hours > 23 or minutes > 59 (clamped). */}
          <Input
            type="text"
            inputMode="numeric"
            value={dosTimeOverride || ''}
            onChange={(e) => {
              if (!setDosTimeOverride) return;
              let v = (e.target.value || '').replace(/[^0-9:]/g, ''); // strip anything not digit or colon
              // Auto-insert colon after 2 digits: "1145" → "11:45"
              if (!v.includes(':') && v.length >= 3) {
                v = `${v.slice(0, 2)}:${v.slice(2, 4)}`;
              }
              // Pad single-digit hour: "9:15" → "09:15" (only on full "H:MM" form)
              if (/^\d:\d{2}$/.test(v)) v = `0${v}`;
              // Clamp: max HH=23, MM=59
              const m = v.match(/^(\d{1,2}):?(\d{0,2})$/);
              if (m) {
                let hh = m[1];
                let mm = m[2] || '';
                if (hh.length === 2 && parseInt(hh) > 23) hh = '23';
                if (mm.length === 2 && parseInt(mm) > 59) mm = '59';
                v = mm ? `${hh}:${mm}` : hh;
              }
              // Cap length to 5 chars ("HH:MM")
              if (v.length > 5) v = v.slice(0, 5);
              setDosTimeOverride(v);
            }}
            onBlur={(e) => {
              // On blur, if user typed just "11" or "1145" without a colon,
              // format it into "HH:MM". Also drop trailing colon if any.
              if (!setDosTimeOverride) return;
              let v = (e.target.value || '').trim();
              if (/^\d{4}$/.test(v)) v = `${v.slice(0, 2)}:${v.slice(2)}`;
              else if (/^\d{3}$/.test(v)) v = `0${v.slice(0, 1)}:${v.slice(1)}`;
              else if (/^\d{2}$/.test(v)) v = `${v}:00`;
              else if (/^\d:\d{2}$/.test(v)) v = `0${v}`;
              else if (v.endsWith(':')) v = v.slice(0, -1);
              if (/^\d{2}:\d{2}$/.test(v)) {
                const [hh, mm] = v.split(':').map(Number);
                if (hh > 23 || mm > 59) v = ''; // reject invalid
              }
              setDosTimeOverride(v);
            }}
            onKeyDown={handleEnterAdvance}
            placeholder="HHMM"
            maxLength={5}
            className={`h-8 text-xs px-1 rounded w-[82px] font-mono tabular-nums text-center ${dosTimeOverride ? 'border-emerald-500/50 bg-emerald-500/5' : ''}`}
            data-testid="setup-header-dos-time"
            title={dosTimeOverride ? `Visit time (24hr): ${dosTimeOverride}` : 'Time of service — type "1145" for 11:45 (24hr military). Leave empty = use scheduled-slot time.'}
          />
          {/* Mar 3, 2026 — Voice mic: providers can say "eleven forty-five",
              "oh nine hundred", or "1145" and the parser fills HH:MM. Uses
              same Deepgram `/api/transcribe` endpoint as main dictation. */}
          <DosTimeMicButton
            disabled={!setDosTimeOverride}
            onTime={(hhmm) => {
              // parseSpokenTime already returns a validated "HH:MM"; feed
              // it in directly (no need to re-run the auto-format logic).
              if (setDosTimeOverride) setDosTimeOverride(hhmm);
            }}
          />
          <label
            className={`flex items-center gap-1 cursor-pointer rounded px-1.5 py-1 text-[10px] font-semibold border transition-colors ${
              lateEntry
                ? 'bg-amber-500/15 border-amber-500/60 text-amber-200'
                : 'border-border text-muted-foreground hover:bg-secondary/40'
            }`}
            data-testid="setup-header-late-entry-label"
            title="Mark this note as a Late Entry (stamps the sig block)"
          >
            <input
              type="checkbox"
              checked={!!lateEntry}
              onChange={(e) => setLateEntry && setLateEntry(e.target.checked)}
              className="w-3 h-3 rounded border-border"
              data-testid="setup-header-late-entry"
            />
            {lateEntry && <AlertCircle className="w-3 h-3 text-amber-300" />}
            <span className="uppercase tracking-wider">Late Entry</span>
          </label>
        </div>
        {/* Visit Type pills — Mode (Video/Phone) is rendered inline RIGHT
            AFTER Virtual so the provider's eye doesn't have to search for it
            past Counseling/Refill (Mar 1, 2026 fix per provider feedback). */}
        <div className="flex items-center gap-0.5 flex-wrap" data-testid="setup-header-visit-type">
          {VISIT_PILLS.map((v) => (
            <React.Fragment key={v.value}>
              <button type="button"
                onClick={() => setVisitType && setVisitType(v.value)}
                className={`text-[10px] px-1.5 py-1 rounded font-medium transition-colors ${
                  visitType === v.value
                    ? 'bg-emerald-500/30 text-emerald-100'
                    : 'text-muted-foreground hover:bg-secondary/70'
                }`}
                data-testid={`setup-header-visit-${v.value}`}
              >{v.label}</button>
              {/* Inject the Mode (Video/Phone) toggle inline after the Virtual pill */}
              {v.value === 'virtual' && renderModeToggle()}
            </React.Fragment>
          ))}
        </div>
        {/* The standalone modality block previously sat AFTER Refill — moved
            inline into the pills loop above. */}
        {/* Appointment Type dropdown */}
        <div className="flex items-center gap-1">
          <span className="elegant-label text-[10px]">Appt</span>
          <select value={appointmentType || ''}
            onChange={(e) => setAppointmentType && setAppointmentType(e.target.value)}
            className="h-8 px-1.5 text-xs rounded border border-border bg-input text-foreground"
            data-testid="setup-header-appt-type"
          >
            <option value="">—</option>
            {APPT_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        {/* Mar 1, 2026 — The compact ICD-10 quick-add chip previously lived
            here. Provider asked to remove it from the demographic bar and
            keep ICD-10 entry consolidated in the right-column tile (and to
            give the Chief Complaint free-text field more room). */}
      </div>

      {/* Row 2 — reason + chief complaint */}
      <div className="flex flex-wrap items-center gap-2 mt-1.5 pt-1.5 border-t border-border/40">
        <Popover open={showReasonPicker} onOpenChange={setShowReasonPicker}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm"
              className={`h-8 px-2.5 gap-1.5 text-xs shrink-0 rounded ${
                reasonSummary
                  ? 'border-emerald-500/40 bg-emerald-500/8 text-emerald-200 hover:bg-emerald-500/15'
                  : 'border-border text-muted-foreground'
              }`}
              data-testid="setup-header-reason-trigger"
              title="Reason for Visit — multi-select"
            >
              <span className="elegant-label text-[10px] opacity-90">Reason</span>
              <span className="font-semibold truncate max-w-[160px]">{reasonSummary || 'Pick'}</span>
              <ChevronDown className="w-3 h-3 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(440px,calc(100vw-2rem))] p-3" data-testid="setup-header-reason-popover">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 pb-2 border-b border-border block">Reason for Visit · select all that apply</Label>
            <div className="flex flex-wrap gap-1.5">
              {(sortedReasonOptions || []).map((opt) => {
                const active = selectedReasons.includes(opt.value);
                return (
                  <button key={opt.value} type="button" onClick={() => toggleReason(opt.value)}
                    className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                      active
                        ? 'bg-emerald-500/25 border border-emerald-400 text-emerald-100'
                        : 'bg-secondary/60 border border-border text-foreground/80 hover:bg-secondary'
                    }`}
                    data-testid={`setup-header-reason-${opt.value}`}
                  >{opt.label}</button>
                );
              })}
            </div>
            {selectedReasons.includes('other') && (
              <Input type="text" placeholder="Describe (optional)"
                value={customReasonForVisit || ''}
                onChange={(e) => setCustomReasonForVisit(e.target.value)}
                className="h-8 text-xs mt-2"
                data-testid="setup-header-custom-reason"
              />
            )}
          </PopoverContent>
        </Popover>

        {/* Chief Complaint — Mar 1, 2026: bumped min width 200 → 320 since
            the duplicate ICD-10 chip was removed from this row, giving CC
            more horizontal room to breathe. */}
        <div className="flex items-center gap-2 flex-1 min-w-[320px]">
          <span className="elegant-label text-[10px] shrink-0">CC</span>
          <Input type="text"
            placeholder="Chief complaint in patient's words (e.g., 'low back pain x 3 mo')"
            value={chiefComplaint || ''}
            onChange={(e) => {
              setChiefComplaint(e.target.value);
              if (autoFilledFields && setAutoFilledFields) {
                setAutoFilledFields((prev) => ({ ...prev, chiefComplaint: false }));
              }
            }}
            onKeyDown={handleEnterAdvance}
            className={`h-8 text-sm flex-1 rounded ${
              autoFilledFields?.chiefComplaint ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-border'
            }`}
            data-testid="setup-header-chief-complaint"
          />
          {chiefComplaint && (
            <button type="button" onClick={() => setChiefComplaint('')}
              className="text-muted-foreground hover:text-foreground shrink-0"
              title="Clear" data-testid="setup-header-cc-clear"
            ><X className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientSetupHeader;
